import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { Accordion } from "react-bootstrap";
import PracticeTest from "./PracticeTest";

/**
 * LMS - Student "My Course".
 *
 * Shows the student's subjects -> chapters -> lectures. Lectures are SEQUENTIAL:
 * a locked lecture cannot be opened until the previous one is completed (the
 * server enforces this too). The player tracks watch time via the YouTube
 * IFrame API and sends progress; "Mark as Completed" unlocks the next lecture.
 *
 * Only the 11-char video id is used; the embed hides YouTube branding/related so
 * the student stays inside the LMS. (Note: no website can fully block a
 * screenshot or screen-recording - this is best-effort deterrence.)
 */
let ytApiLoading = null;
function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (ytApiLoading) return ytApiLoading;
  ytApiLoading = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) try { prev(); } catch (e) {}
      resolve();
    };
  });
  return ytApiLoading;
}

export default function MyCourse() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("studentToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // the lecture being watched
  const [canComplete, setCanComplete] = useState(false);
  const [test, setTest] = useState(null); // { subjectId, subjectName, mode } for the self-check

  // All of a subject's lectures finished? (gates the Quick Test)
  const subjectDone = (s) => {
    const lecs = (s.chapters || []).flatMap((ch) => ch.lectures || []);
    return lecs.length > 0 && lecs.every((l) => l.completed === true);
  };

  // One subject card (chapters -> lectures, material, self-check tests).
  const renderSubject = (s) => (
    <div key={s.id} className="card mb-3">
      <div className="card-header">
        <b>{s.name}</b> {s.code ? <small className="text-muted">({s.code})</small> : null}
      </div>
      <div className="card-body">
        {s.chapters.length === 0 ? (
          <span className="text-muted">No lectures yet.</span>
        ) : (
          s.chapters.map((ch) => (
            <div key={ch.id} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{ch.title}</div>
              <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
                {ch.lectures.map((lec) => (
                  <li
                    key={lec.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderBottom: "1px solid #f0f0f0",
                      opacity: lec.locked ? 0.55 : 1,
                    }}
                  >
                    <span>
                      {lec.completed ? "✅ " : lec.locked ? "🔒 " : "▶ "}
                      {lec.title}
                    </span>
                    <button
                      className={`btn btn-sm ${lec.locked ? "btn-light" : "btn-outline-primary"}`}
                      disabled={lec.locked}
                      onClick={() => openLecture(lec)}
                    >
                      {lec.completed ? "Rewatch" : lec.locked ? "Locked" : "Watch"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}

        {/* study material */}
        {s.materials && s.materials.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Study Material</div>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {s.materials.map((m) => (
                <li key={m.id}>
                  <a href={m.file} target="_blank" rel="noreferrer">
                    {m.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* self-check tests (nothing is stored) */}
        <div style={{ marginTop: 12, borderTop: "1px solid #f0f0f0", paddingTop: 10 }}>
          <button
            className="btn btn-sm btn-outline-secondary mr-2"
            onClick={() => setTest({ subjectId: s.id, subjectName: s.name, mode: "practice" })}
          >
            Practice Test
          </button>
          {subjectDone(s) ? (
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() => setTest({ subjectId: s.id, subjectName: s.name, mode: "quick" })}
            >
              Quick Test
            </button>
          ) : (
            <button className="btn btn-sm btn-light" disabled title="Finish all the videos of this subject to unlock">
              🔒 Quick Test
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const activeRef = useRef(null);

  const load = async () => {
    try {
      const res = await dispatch(getDataAPI("student/lms/my-course", token()));
      setData(res.data.data);
    } catch (e) {
      alert({ error: "Could not load your course." });
    }
  };

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      try { playerRef.current && playerRef.current.destroy(); } catch (e) {}
    };
  }, []);

  // anti-piracy (best effort): block right-click + text selection on this page
  useEffect(() => {
    const noMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", noMenu);
    return () => document.removeEventListener("contextmenu", noMenu);
  }, []);

  const saveProgress = async (seconds, pos) => {
    if (!activeRef.current) return;
    try {
      await dispatch(
        postDataAPI(
          "student/lms/progress",
          { lecture_id: activeRef.current.id, watched_seconds: Math.floor(seconds), last_position: Math.floor(pos) },
          token()
        )
      );
    } catch (e) {
      /* non-fatal */
    }
  };

  const openLecture = async (lec) => {
    if (lec.locked) {
      alert({ error: "Finish the previous lecture first." });
      return;
    }
    setActive(lec);
    activeRef.current = lec;
    setCanComplete(lec.completed === true);

    await loadYT();
    // (re)create the player
    try { playerRef.current && playerRef.current.destroy(); } catch (e) {}
    if (pollRef.current) clearInterval(pollRef.current);

    playerRef.current = new window.YT.Player("lms-player", {
      videoId: lec.youtube_id,
      playerVars: {
        modestbranding: 1,
        rel: 0,
        controls: 1,
        disablekb: 1,
        fs: 1,
        origin: window.location.origin,
      },
      events: {
        onStateChange: (ev) => {
          // 1 = playing -> poll; 0 = ended -> allow complete
          if (ev.data === 1) {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(() => {
              try {
                const p = playerRef.current;
                const cur = p.getCurrentTime();
                const dur = p.getDuration();
                saveProgress(cur, cur);
                if (dur > 0 && cur >= dur * 0.9) setCanComplete(true);
              } catch (e) {}
            }, 5000);
          } else if (ev.data === 0) {
            setCanComplete(true);
            if (pollRef.current) clearInterval(pollRef.current);
            try {
              const cur = playerRef.current.getDuration();
              saveProgress(cur, cur);
            } catch (e) {}
          } else {
            if (pollRef.current) clearInterval(pollRef.current);
          }
        },
      },
    });
  };

  const markComplete = async () => {
    try {
      await dispatch(postDataAPI("student/lms/complete", { lecture_id: active.id }, token()));
      alert({ success: "Lecture completed." });
      await load();
      setActive(null);
      activeRef.current = null;
      if (pollRef.current) clearInterval(pollRef.current);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not mark complete." });
    }
  };

  if (!data) {
    return <div style={{ padding: 20 }}>Loading your course…</div>;
  }

  return (
    <div style={{ padding: 20, userSelect: "none" }}>
      <h4>{data.course ? data.course.name : "My Course"}</h4>

      <div style={{ margin: "10px 0 20px" }}>
        <div style={{ background: "#eee", borderRadius: 6, height: 14, overflow: "hidden" }}>
          <div
            style={{
              width: `${data.progress.percent}%`,
              background: "#243386",
              height: "100%",
              transition: "width .3s",
            }}
          />
        </div>
        <small>
          Overall progress: {data.progress.percent}% ({data.progress.completed}/{data.progress.total} lectures)
        </small>
      </div>

      {/* Player */}
      {active && (
        <div style={{ marginBottom: 24, border: "1px solid #ddd", borderRadius: 8, padding: 12 }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <b>{active.title}</b>
            <button
              className="btn btn-sm btn-success"
              disabled={!canComplete}
              onClick={markComplete}
              title={canComplete ? "" : "Watch the video to the end to enable"}
            >
              {active.completed ? "Completed ✓" : "Mark as Completed"}
            </button>
          </div>
          <div style={{ position: "relative", paddingTop: "56.25%" }}>
            <div
              id="lms-player"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
            />
          </div>
          {!canComplete && (
            <small className="text-muted">Watch the full video to mark it complete and unlock the next one.</small>
          )}
        </div>
      )}

      {/* Subjects grouped like the Assessment screen: course-wise = flat;
          semester/year = an accordion per Semester/Year. */}
      {data.subjects.length === 0 ? (
        <p className="text-muted">No content has been added to your course yet.</p>
      ) : (
        (() => {
          const course = data.course || {};
          const courseWise =
            course.exam_type === "course_wise" || String(course.course_type) === "1";

          if (courseWise) return data.subjects.map(renderSubject);

          const label = String(course.course_type) === "2" ? "Semester" : "Year";
          const groups = {};
          data.subjects.forEach((s) => {
            const k = String(s.semester || "1");
            (groups[k] = groups[k] || []).push(s);
          });
          return Object.keys(groups)
            .sort((a, b) => Number(a) - Number(b))
            .map((k, i) => (
              <Accordion key={k} defaultActiveKey={i === 0 ? "0" : null} className="mb-3">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    {label} {k}
                  </Accordion.Header>
                  <Accordion.Body className="p-2">
                    {groups[k].map(renderSubject)}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            ));
        })()
      )}

      <PracticeTest
        show={!!test}
        subjectId={test ? test.subjectId : null}
        subjectName={test ? test.subjectName : ""}
        mode={test ? test.mode : "practice"}
        onClose={() => setTest(null)}
      />
    </div>
  );
}
